// Invite tab — invite form (3-step atomic sequence) + pending-invitations table.
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import { MONO_FONT, PROJECT_SCOPES, getRoleMeta } from "../data/mockUsersRoles";
import { RolePill, ScopePill } from "./shared";
import { createInvitation, resendInvite, getMyOrg, listRoles } from "../api";

const InviteTab = ({ pending, setPending, setUsers, showToast, onDone, reload }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [projects, setProjects] = useState(["MUHC"]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [orgSender, setOrgSender] = useState(null);
  // Assignable roles come from the BFF (live Keycloak ∩ DB catalog), NOT from
  // hard-coded mock metadata — so we can only ever offer a role that actually
  // resolves in the realm (see audit H1). Filter to roles present in Keycloak.
  const [roleOptions, setRoleOptions] = useState([]);

  // Show the address invites are sent from (per-org sender, Phase 4).
  useEffect(() => {
    getMyOrg().then(setOrgSender).catch(() => setOrgSender(null));
  }, []);

  // Load assignable roles (only those that exist in the tenant's Keycloak, so
  // the atomic invite's role-resolution step can't fail on an unknown role).
  useEffect(() => {
    listRoles()
      .then((roles) =>
        setRoleOptions(
          (roles || [])
            .filter((r) => r.inKeycloak !== false)
            .map((r) => ({ key: r.name, label: r.displayName || r.name }))
        )
      )
      .catch(() => setRoleOptions([]));
  }, []);

  const labelSx = {
    fontSize: "0.625rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: colors.grey[500],
    mb: 0.5,
  };

  const reset = () => {
    setEmail("");
    setName("");
    setRole("");
    setProjects(["MUHC"]);
    setMessage("");
  };

  // The invite is one atomic transaction on the BFF: create → assign → email
  // (POST /api/invitations rolls back on failure — see API doc §3).
  const handleSend = async () => {
    if (!email.trim()) return showToast("Email address is required", "warning");
    if (!email.includes("@")) return showToast("Please enter a valid email address", "warning");
    if (!role) return showToast("Please select a role", "warning");

    setSending(true);
    try {
      const created = await createInvitation({
        email: email.trim(),
        name: name.trim(),
        role,
        projects,
        message: message.trim(),
      });
      // Reflect the new pending invite immediately, then refresh from the server.
      setPending((prev) => [
        {
          id: created.id,
          email: created.email,
          role: created.role,
          projects: created.projects || [],
          sent: created.sent,
          expiresInH: created.expiresInH ?? 24,
        },
        ...prev,
      ]);
      showToast(`Invitation sent to ${created.email} (${role})`, "success");
      reset();
      await reload?.();
      setTimeout(() => onDone?.(), 900);
    } catch (err) {
      console.error("[InviteTab] invite failed:", err);
      showToast(`Could not send invitation: ${err.message || "server error"}`, "error");
    } finally {
      setSending(false);
    }
  };

  // BFF call: PUT /api/users/{id}/invite-email  (§3.5 — resend)
  const handleResend = async (invite) => {
    setResendingId(invite.id);
    try {
      await resendInvite(invite.id);
      showToast(`Resent invitation to ${invite.email}`, "success");
    } catch (err) {
      console.error("[InviteTab] resend failed:", err);
      showToast(`Could not resend to ${invite.email}: ${err.message || "server error"}`, "error");
    } finally {
      setResendingId(null);
    }
  };

  return (
    <Box>
      {/* Invite panel */}
      <Box
        sx={{
          backgroundColor: "var(--fx-paper)",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
          mb: 2.5,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: 'var(--fx-text-primary)' }}>
            Invite a new user
          </Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: colors.grey[500] }}>
            {orgSender?.hasOwnSender
              ? `Invitation sent from ${orgSender.sender}`
              : "Invitation sent via the platform"}
          </Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={labelSx}>Email address *</Typography>
              <TextField fullWidth size="small" placeholder="user@institution.ca" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={labelSx}>Full name</Typography>
              <TextField fullWidth size="small" placeholder="Given and family name" value={name} onChange={(e) => setName(e.target.value)} />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={labelSx}>Keycloak role *</Typography>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={role}
                onChange={(e) => setRole(e.target.value)}
                renderValue={(v) => (v ? (roleOptions.find((o) => o.key === v)?.label || getRoleMeta(v).label) : <Box component="span" sx={{ color: colors.grey[500] }}>, Select a role, </Box>)}
              >
                {roleOptions.length === 0 && (
                  <MenuItem disabled sx={{ fontSize: "0.8125rem" }}>
                    <ListItemText
                      primary="No assignable roles found"
                      secondary="Create a role in the Roles tab first"
                      primaryTypographyProps={{ fontSize: "0.8125rem" }}
                      secondaryTypographyProps={{ fontSize: "0.625rem" }}
                    />
                  </MenuItem>
                )}
                {roleOptions.map(({ key, label }) => (
                  <MenuItem key={key} value={key} sx={{ fontSize: "0.8125rem" }}>
                    <ListItemText
                      primary={label}
                      secondary={key}
                      primaryTypographyProps={{ fontSize: "0.8125rem" }}
                      secondaryTypographyProps={{ fontSize: "0.625rem" }}
                    />
                  </MenuItem>
                ))}
              </Select>
              <Typography sx={{ fontSize: "0.625rem", color: colors.grey[500], mt: 0.4 }}>
                Maps to a Keycloak realm role and an authorization policy scope.
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={labelSx}>Site scope</Typography>
              <Select
                multiple
                fullWidth
                size="small"
                value={projects}
                onChange={(e) => setProjects(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
                input={<OutlinedInput />}
                renderValue={(sel) => (sel.length ? sel.join(", ") : "Not set")}
              >
                {PROJECT_SCOPES.map((p) => (
                  <MenuItem key={p} value={p} sx={{ fontSize: "0.8125rem" }}>
                    <Checkbox size="small" checked={projects.indexOf(p) > -1} />
                    <ListItemText primary={p} primaryTypographyProps={{ fontSize: "0.8125rem" }} />
                  </MenuItem>
                ))}
              </Select>
              <Typography sx={{ fontSize: "0.625rem", color: colors.grey[500], mt: 0.4 }}>
                Sets the <code>site_ids</code> JWT claim. Required for Site Operator.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography sx={labelSx}>Message (optional)</Typography>
            <TextField fullWidth size="small" placeholder="Add a personal note to the invitation email…" value={message} onChange={(e) => setMessage(e.target.value)} />
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Button variant="contained" onClick={handleSend} disabled={sending}>
              {sending ? "Sending…" : "Send invitation"}
            </Button>
            <Button variant="outlined" onClick={onDone} disabled={sending}>Cancel</Button>
            <Typography sx={{ ml: "auto", fontSize: "0.6875rem", color: colors.grey[500] }}>
              User receives a Keycloak one-time setup link valid for 24 hours
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Pending invitations */}
      <Box
        sx={{
          backgroundColor: "var(--fx-paper)",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: 'var(--fx-text-primary)' }}>
            Pending invitations
          </Typography>
          <Box
            component="span"
            sx={{
              fontSize: "0.625rem",
              fontWeight: 600,
              color: "#d97706",
              backgroundColor: "rgba(217,119,6,0.1)",
              border: "1px solid rgba(217,119,6,0.25)",
              borderRadius: 1,
              px: 0.75,
              py: 0.25,
            }}
          >
            {pending.length} pending
          </Box>
        </Box>

        {pending.length ? (
          <Table size="small" sx={{ "& td, & th": { fontSize: "0.75rem", borderColor: theme.palette.divider } }}>
            <TableHead>
              <TableRow sx={{ "& th": { color: colors.grey[500], fontWeight: 600, py: 1 } }}>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Site scope</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pending.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ color: 'var(--fx-text-primary)' }}>{p.email}</TableCell>
                  <TableCell><RolePill roleKey={p.role} /></TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {p.projects.map((s) => <ScopePill key={s} label={s} />)}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: MONO_FONT, color: colors.grey[500] }}>{p.sent}</TableCell>
                  <TableCell
                    sx={{
                      fontFamily: MONO_FONT,
                      color: p.expiresInH <= 8 ? "#d97706" : colors.grey[500],
                      fontWeight: p.expiresInH <= 8 ? 600 : 400,
                    }}
                  >
                    Expires in {p.expiresInH}h
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" disabled={resendingId === p.id} onClick={() => handleResend(p)} sx={{ minHeight: 26, py: 0.25, fontSize: "0.6875rem" }}>
                      {resendingId === p.id ? "Sending…" : "Resend"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography sx={{ p: 3, textAlign: "center", fontSize: "0.75rem", color: colors.grey[500] }}>
            No pending invitations
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default InviteTab;
